import React from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .bk-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .bk-page { font-family: 'Inter', sans-serif; color: #0f172a; background: #ffffff; width: 100%; }

  /* ── Hero ── */
  .bk-hero { position: relative; padding: 100px 24px 120px; overflow: hidden; background: #fff; }
  .bk-hero-blob1 { position: absolute; top: -8%; right: -4%; width: 560px; height: 560px; border-radius: 50%; background: radial-gradient(circle, rgba(99,102,241,0.45), rgba(139,92,246,0.2)); filter: blur(80px); pointer-events: none; }
  .bk-hero-blob2 { position: absolute; bottom: -12%; left: -8%; width: 640px; height: 640px; border-radius: 50%; background: radial-gradient(circle, rgba(59,130,246,0.38), rgba(99,102,241,0.18)); filter: blur(80px); pointer-events: none; }
  .bk-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .bk-hero-inner { grid-template-columns: 1fr; } .bk-hero-card { display: none; } }

  .bk-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 999px; background: #eff6ff; border: 1px solid #bfdbfe; font-size: 13px; font-weight: 500; color: #1e40af; margin-bottom: 28px; }
  .bk-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: #3b82f6; display: inline-block; animation: bk-pulse 2s ease-in-out infinite; }
  @keyframes bk-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.5); } 50% { box-shadow: 0 0 0 6px rgba(59,130,246,0); } }

  .bk-hero h1 { font-size: clamp(1.95rem, 3.75vw, 3.15rem); font-weight: 900; line-height: 1.1; letter-spacing: -0.03em; color: #0f172a; margin-bottom: 24px; }
  .bk-gradient-text { background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .bk-hero-sub { font-size: 1.15rem; color: #475569; line-height: 1.8; margin-bottom: 40px; font-weight: 400; }
  .bk-btn-group { display: flex; gap: 16px; flex-wrap: wrap; }
  .bk-btn-primary { padding: 14px 28px; background: #0f172a; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; }
  .bk-btn-primary:hover { background: #2563eb; transform: translateY(-1px); }
  .bk-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .bk-btn-secondary:hover { border-color: #93c5fd; background: #eff6ff; color: #2563eb; }

  /* Hero Dashboard Card */
  .bk-hero-card { background: #fff; border-radius: 24px; border: 1px solid #e2e8f0; padding: 32px; box-shadow: 0 20px 60px rgba(37,99,235,0.1), 0 4px 16px rgba(0,0,0,0.04); position: relative; }
  .bk-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
  .bk-card-title-label { font-size: 13px; color: #94a3b8; font-weight: 500; margin-bottom: 3px; }
  .bk-card-title-value { font-size: 17px; font-weight: 700; color: #0f172a; }
  .bk-card-icon { width: 48px; height: 48px; background: #eff6ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .bk-tx-item { display: flex; justify-content: space-between; align-items: center; padding: 12px 14px; border-radius: 12px; background: #f8fafc; border: 1px solid #f1f5f9; margin-bottom: 10px; }
  .bk-tx-left { display: flex; align-items: center; gap: 12px; }
  .bk-tx-icon { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
  .bk-tx-name { font-size: 13.5px; font-weight: 600; color: #334155; }
  .bk-tx-date { font-size: 11.5px; color: #94a3b8; margin-top: 1px; }
  .bk-tx-amount { font-size: 14px; font-weight: 700; }
  .bk-tx-amount.income { color: #16a34a; }
  .bk-tx-amount.expense { color: #dc2626; }
  .bk-card-metric { margin-top: 16px; padding: 20px; background: linear-gradient(135deg, #2563eb, #7c3aed); border-radius: 16px; display: flex; justify-content: space-between; align-items: center; }
  .bk-card-metric-label { font-size: 12px; color: rgba(255,255,255,0.8); font-weight: 500; margin-bottom: 4px; }
  .bk-card-metric-value { font-size: 26px; font-weight: 900; color: #fff; letter-spacing: -0.02em; }
  .bk-card-metric-badge { background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); border-radius: 999px; padding: 5px 12px; font-size: 12px; font-weight: 600; color: #fff; }

  /* ── Stats Bar ── */
  .bk-stats { background: #fff; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; }
  .bk-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); max-width: 1200px; margin: 0 auto; }
  @media (max-width: 768px) { .bk-stats-grid { grid-template-columns: 1fr 1fr; } }
  .bk-stat-item { padding: 36px 24px; text-align: center; border-right: 1px solid #f1f5f9; }
  .bk-stat-item:last-child { border-right: none; }
  @media (max-width: 768px) { .bk-stat-item:nth-child(2n) { border-right: none; } .bk-stat-item { border-bottom: 1px solid #f1f5f9; } }
  .bk-stat-num { font-size: 2.1rem; font-weight: 900; color: #2563eb; letter-spacing: -0.03em; line-height: 1; margin-bottom: 8px; }
  .bk-stat-label { font-size: 0.82rem; color: #64748b; font-weight: 500; line-height: 1.4; }

  /* ── Shared Section Atoms ── */
  .bk-section { padding: 96px 24px; }
  .bk-container { max-width: 1200px; margin: 0 auto; }
  .bk-section-header { text-align: center; max-width: 720px; margin: 0 auto 64px; }
  .bk-section-header h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 16px; line-height: 1.2; }
  .bk-section-header p { font-size: 1.08rem; color: #64748b; line-height: 1.78; }
  .bk-kicker { font-size: 12.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #2563eb; margin-bottom: 12px; }

  /* ── Services Grid ── */
  .bk-services-bg { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.6); border-bottom: 1px solid rgba(226,232,240,0.6); }
  .bk-services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 960px) { .bk-services-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .bk-services-grid { grid-template-columns: 1fr; } }
  .bk-svc-card { background: #fff; padding: 36px 32px; border-radius: 20px; border: 1px solid rgba(226,232,240,0.7); box-shadow: 0 1px 4px rgba(0,0,0,0.03); transition: all 0.3s; position: relative; overflow: hidden; cursor: pointer; }
  .bk-svc-card:hover { box-shadow: 0 16px 48px rgba(0,0,0,0.09); transform: translateY(-3px); border-color: transparent; }
  .bk-svc-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; opacity: 0; transition: opacity 0.3s; border-radius: 20px 20px 0 0; }
  .bk-svc-card:hover::before { opacity: 1; }
  .bk-svc-card.c-blue::before    { background: linear-gradient(90deg, #2563eb, #4f46e5); }
  .bk-svc-card.c-indigo::before  { background: linear-gradient(90deg, #4f46e5, #7c3aed); }
  .bk-svc-card.c-violet::before  { background: linear-gradient(90deg, #7c3aed, #c026d3); }
  .bk-svc-card.c-teal::before    { background: linear-gradient(90deg, #0d9488, #0284c7); }
  .bk-svc-card.c-sky::before     { background: linear-gradient(90deg, #0ea5e9, #2563eb); }
  .bk-svc-card.c-emerald::before { background: linear-gradient(90deg, #059669, #0d9488); }
  .bk-svc-icon { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 22px; font-size: 24px; }
  .bk-svc-icon.c-blue    { background: #eff6ff; }
  .bk-svc-icon.c-indigo  { background: #eef2ff; }
  .bk-svc-icon.c-violet  { background: #f5f3ff; }
  .bk-svc-icon.c-teal    { background: #f0fdfa; }
  .bk-svc-icon.c-sky     { background: #e0f2fe; }
  .bk-svc-icon.c-emerald { background: #ecfdf5; }
  .bk-svc-card h3 { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin-bottom: 12px; }
  .bk-svc-card p { font-size: 0.91rem; color: #64748b; line-height: 1.72; margin-bottom: 22px; }
  .bk-svc-tags { display: flex; flex-wrap: wrap; gap: 7px; }
  .bk-svc-tag { padding: 4px 11px; font-size: 11px; font-weight: 600; border-radius: 999px; background: rgba(226,232,240,0.6); color: #475569; transition: all 0.2s; }
  .bk-svc-card:hover .bk-svc-tag { background: #eff6ff; color: #2563eb; }
  .bk-svc-arrow { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #2563eb; margin-top: 16px; opacity: 0; transform: translateX(-4px); transition: opacity 0.2s, transform 0.2s; }
  .bk-svc-card:hover .bk-svc-arrow { opacity: 1; transform: translateX(0); }

  /* ── Feature Spotlight ── */
  .bk-spotlight-bg { background: #fff; }
  .bk-spotlight-inner { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 32px; padding: 72px 64px; display: flex; gap: 80px; align-items: center; flex-wrap: wrap; }
  @media (max-width: 960px) { .bk-spotlight-inner { padding: 48px 40px; gap: 48px; } }
  .bk-spotlight-left { flex: 1; min-width: 280px; }
  .bk-spotlight-left h2 { font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 14px; line-height: 1.22; }
  .bk-spotlight-left > p { font-size: 1.03rem; color: #64748b; line-height: 1.8; margin-bottom: 44px; }
  .bk-feat-list { display: flex; flex-direction: column; gap: 28px; }
  .bk-feat { display: flex; gap: 16px; align-items: flex-start; }
  .bk-feat-icon { flex-shrink: 0; width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .bk-feat-icon.c-blue   { background: #eff6ff; }
  .bk-feat-icon.c-indigo { background: #eef2ff; }
  .bk-feat-icon.c-teal   { background: #f0fdfa; }
  .bk-feat h4 { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 5px; }
  .bk-feat p { font-size: 0.9rem; color: #64748b; line-height: 1.68; }
  .bk-spotlight-right { flex: 1; min-width: 280px; }
  .bk-visual-card { background: linear-gradient(145deg, #1e40af, #4f46e5); border-radius: 24px; padding: 36px; position: relative; overflow: hidden; min-height: 390px; display: flex; flex-direction: column; gap: 20px; justify-content: space-between; }
  .bk-visual-dots { position: absolute; inset: 0; opacity: 0.12; background-image: radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 0); background-size: 24px 24px; pointer-events: none; }
  .bk-visual-metric-box { background: rgba(255,255,255,0.15); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.25); border-radius: 18px; padding: 22px; }
  .bk-vm-label { font-size: 12px; color: rgba(255,255,255,0.75); font-weight: 500; margin-bottom: 6px; }
  .bk-vm-value { font-size: 36px; font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1; }
  .bk-vm-delta { font-size: 12px; color: rgba(255,255,255,0.85); margin-top: 6px; display: flex; align-items: center; gap: 4px; }
  .bk-vm-delta span { background: rgba(255,255,255,0.2); border-radius: 999px; padding: 2px 8px; font-weight: 600; }
  .bk-visual-insight { background: #fff; border-radius: 18px; padding: 22px; box-shadow: 0 8px 32px rgba(0,0,0,0.14); }
  .bk-vi-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .bk-vi-title { font-weight: 700; color: #1e293b; font-size: 14px; }
  .bk-vi-badge { font-size: 11px; font-weight: 700; color: #2563eb; background: #dbeafe; padding: 4px 10px; border-radius: 999px; letter-spacing: 0.04em; }
  .bk-bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
  .bk-bar-label { font-size: 11.5px; color: #64748b; width: 82px; flex-shrink: 0; }
  .bk-bar-track { flex: 1; height: 7px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
  .bk-bar-fill { height: 100%; border-radius: 999px; }
  .bk-bar-val { font-size: 12px; font-weight: 600; color: #334155; width: 32px; text-align: right; }

  /* ── Process Steps ── */
  .bk-process-bg { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.6); }
  .bk-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; position: relative; }
  @media (max-width: 900px) { .bk-steps { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .bk-steps { grid-template-columns: 1fr; } }
  .bk-steps::before { content: ''; position: absolute; top: 32px; left: calc(12.5% + 8px); right: calc(12.5% + 8px); height: 2px; background: linear-gradient(90deg, #2563eb, #7c3aed); z-index: 0; opacity: 0.3; }
  @media (max-width: 900px) { .bk-steps::before { display: none; } }
  .bk-step { text-align: center; position: relative; z-index: 1; }
  .bk-step-num { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #7c3aed); color: #fff; font-size: 1.4rem; font-weight: 900; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; box-shadow: 0 8px 24px rgba(37,99,235,0.35); }
  .bk-step h3 { font-size: 1.05rem; font-weight: 700; color: #1e293b; margin-bottom: 12px; }
  .bk-step p { font-size: 0.88rem; color: #64748b; line-height: 1.72; max-width: 240px; margin: 0 auto; }

  /* ── Trust / Accuracy (dark) ── */
  .bk-trust-bg { background: #0f172a; color: #f8fafc; position: relative; overflow: hidden; }
  .bk-trust-glow1 { position: absolute; top: -5%; right: 20%; width: 600px; height: 600px; background: radial-gradient(circle, rgba(37,99,235,0.22), transparent 70%); pointer-events: none; }
  .bk-trust-glow2 { position: absolute; bottom: -15%; left: -8%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(124,58,237,0.16), transparent 70%); pointer-events: none; }
  .bk-trust-bg .bk-kicker { color: #93c5fd; }
  .bk-trust-bg h2 { color: #fff; }
  .bk-trust-bg .bk-section-header p { color: #94a3b8; }
  .bk-trust-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 960px) { .bk-trust-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .bk-trust-grid { grid-template-columns: 1fr; } }
  .bk-trust-card { background: rgba(30,41,59,0.5); border: 1px solid rgba(71,85,105,0.4); border-radius: 20px; padding: 32px; transition: border-color 0.3s, background 0.3s; }
  .bk-trust-card:hover { border-color: rgba(37,99,235,0.5); background: rgba(30,41,59,0.75); }
  .bk-trust-icon-wrap { font-size: 28px; margin-bottom: 18px; display: block; }
  .bk-trust-card h4 { font-size: 1.02rem; font-weight: 700; color: #fff; margin-bottom: 10px; }
  .bk-trust-card p { font-size: 0.89rem; color: #94a3b8; line-height: 1.72; }

  /* ── Testimonials ── */
  .bk-testimonials-bg { background: #fff; }
  .bk-testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 960px) { .bk-testimonials-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .bk-testimonials-grid { grid-template-columns: 1fr; } }
  .bk-tcard { background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 20px; padding: 32px; transition: all 0.3s; display: flex; flex-direction: column; }
  .bk-tcard:hover { background: #fff; box-shadow: 0 14px 44px rgba(0,0,0,0.08); border-color: #e2e8f0; }
  .bk-stars { color: #f59e0b; font-size: 15px; letter-spacing: 2px; margin-bottom: 18px; }
  .bk-tcard-text { font-size: 0.93rem; color: #334155; line-height: 1.78; font-style: italic; margin-bottom: 24px; flex: 1; }
  .bk-tcard-author { display: flex; align-items: center; gap: 12px; }
  .bk-author-avatar { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .bk-author-name { font-size: 13.5px; font-weight: 700; color: #1e293b; }
  .bk-author-role { font-size: 12px; color: #94a3b8; margin-top: 1px; }

  /* ── CTA ── */
  .bk-cta-bg { background: #fff; border-top: 1px solid #f1f5f9; }
  .bk-cta-inner { background: #0f172a; border-radius: 32px; padding: 88px 56px; text-align: center; position: relative; overflow: hidden; max-width: 1000px; margin: 0 auto; }
  @media (max-width: 640px) { .bk-cta-inner { padding: 52px 28px; border-radius: 24px; } }
  .bk-cta-glow1 { position: absolute; top: -20%; right: -5%; width: 400px; height: 400px; background: radial-gradient(circle, rgba(37,99,235,0.32), transparent 65%); pointer-events: none; }
  .bk-cta-glow2 { position: absolute; bottom: -20%; left: -5%; width: 400px; height: 400px; background: radial-gradient(circle, rgba(124,58,237,0.22), transparent 65%); pointer-events: none; }
  .bk-cta-inner h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 18px; position: relative; z-index: 1; }
  .bk-cta-inner > p { font-size: 1.12rem; color: #94a3b8; font-weight: 300; max-width: 560px; margin: 0 auto 40px; line-height: 1.75; position: relative; z-index: 1; }
  .bk-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; margin-bottom: 32px; }
  .bk-cta-primary { padding: 15px 34px; background: #2563eb; color: #fff; border: none; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; box-shadow: 0 4px 20px rgba(37,99,235,0.45); }
  .bk-cta-primary:hover { background: #3b82f6; transform: translateY(-1px); }
  .bk-cta-secondary { padding: 15px 34px; background: #1e293b; color: #cbd5e1; border: 1px solid #334155; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .bk-cta-secondary:hover { background: #334155; color: #fff; }
  .bk-cta-trust { display: flex; align-items: center; justify-content: center; gap: 28px; flex-wrap: wrap; position: relative; z-index: 1; }
  .bk-cta-trust-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #64748b; }
  .bk-cta-trust-item .check { color: #60a5fa; font-weight: 800; }

  /* ── Service Sub-Page Hero ── */
  .bk-svchero { position: relative; padding: 80px 24px 100px; overflow: hidden; background: #fff; }
  .bk-svchero-blob1 { position: absolute; top: -10%; right: -5%; width: 480px; height: 480px; border-radius: 50%; background: radial-gradient(circle, rgba(99,102,241,0.42), rgba(139,92,246,0.18)); filter: blur(70px); pointer-events: none; }
  .bk-svchero-blob2 { position: absolute; bottom: -15%; left: -6%; width: 560px; height: 560px; border-radius: 50%; background: radial-gradient(circle, rgba(59,130,246,0.32), rgba(99,102,241,0.15)); filter: blur(70px); pointer-events: none; }
  .bk-svchero-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .bk-svchero-inner { grid-template-columns: 1fr; } .bk-svchero-card { display: none; } }
  .bk-svchero-card { background: #fff; border-radius: 24px; border: 1px solid #e2e8f0; padding: 28px; box-shadow: 0 20px 60px rgba(37,99,235,0.1), 0 4px 16px rgba(0,0,0,0.04); }
  .bk-svchero-card-hdr { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #f1f5f9; }
  .bk-svchero-hdr-text { font-size: 15px; font-weight: 700; color: #0f172a; }
  .bk-svchero-hdr-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
  .bk-svchero-hdr-icon { width: 42px; height: 42px; background: #eff6ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 18px; }
  .bk-svchero-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 10px; background: #f8fafc; border: 1px solid #f1f5f9; margin-bottom: 8px; }
  .bk-svchero-row-label { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #475569; font-weight: 500; }
  .bk-svchero-row-val { font-size: 12.5px; font-weight: 700; color: #2563eb; }
  .bk-svchero-metric { margin-top: 14px; padding: 18px 20px; background: linear-gradient(135deg, #2563eb, #7c3aed); border-radius: 14px; display: flex; justify-content: space-between; align-items: center; }
  .bk-svchero-metric-label { font-size: 11px; color: rgba(255,255,255,0.8); font-weight: 500; margin-bottom: 3px; }
  .bk-svchero-metric-value { font-size: 22px; font-weight: 900; color: #fff; letter-spacing: -0.02em; }
  .bk-svchero-metric-badge { background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); border-radius: 999px; padding: 4px 10px; font-size: 11px; font-weight: 600; color: #fff; white-space: nowrap; }
  .bk-svchero-bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 9px; }
  .bk-svchero-bar-label { font-size: 11px; color: #64748b; width: 76px; flex-shrink: 0; }
  .bk-svchero-bar-track { flex: 1; height: 6px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
  .bk-svchero-bar-fill { height: 100%; border-radius: 999px; }
  .bk-svchero-bar-val { font-size: 11px; font-weight: 600; color: #334155; width: 30px; text-align: right; }
  .bk-svchero-insight-title { font-size: 12px; font-weight: 700; color: #334155; margin-bottom: 12px; }

  /* ── Module / Service Sub-Pages ── */
  .bk-mod { background: #f8fafc; min-height: 100vh; }
  .bk-mod-inner { max-width: 1100px; margin: 0 auto; padding: 64px 24px 96px; }
  .bk-back-btn { display: inline-flex; align-items: center; gap: 7px; padding: 7px 16px; border-radius: 999px; border: 1px solid #e2e8f0; background: #fff; color: #475569; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s; margin-bottom: 32px; }
  .bk-back-btn:hover { background: #eff6ff; border-color: #93c5fd; color: #2563eb; }
  .bk-mod-kicker { font-size: 12.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #2563eb; margin-bottom: 10px; }
  .bk-mod-title { font-size: clamp(1.5rem, 3vw, 2.4rem); font-weight: 900; color: #0f172a; letter-spacing: -0.03em; margin-bottom: 18px; line-height: 1.12; }
  .bk-mod-desc { font-size: 1.08rem; color: #64748b; line-height: 1.8; max-width: 680px; margin-bottom: 28px; }
  .bk-mod-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 56px; }
  .bk-mod-tag { padding: 5px 14px; border-radius: 999px; background: #eff6ff; color: #2563eb; font-size: 12px; font-weight: 600; border: 1px solid #bfdbfe; }
  .bk-mod-section { margin-bottom: 56px; }
  .bk-mod-section h3 { font-size: 1.45rem; font-weight: 800; color: #2563eb; margin-bottom: 20px; letter-spacing: -0.02em; }
  .bk-mod-split { display: grid; grid-template-columns: minmax(0,1.2fr) minmax(0,1fr); gap: 56px; align-items: start; margin-bottom: 56px; }
  @media (max-width: 900px) { .bk-mod-split { grid-template-columns: 1fr; gap: 36px; } }
  .bk-mod-split-body { font-size: 0.96rem; color: #64748b; line-height: 1.78; margin-bottom: 28px; }
  .bk-mod-check-list { display: flex; flex-direction: column; gap: 12px; }
  .bk-mod-check { display: flex; align-items: flex-start; gap: 10px; font-size: 0.93rem; color: #334155; line-height: 1.6; }
  .bk-mod-check-icon { color: #2563eb; font-weight: 800; flex-shrink: 0; font-size: 16px; margin-top: 1px; }
  .bk-mod-right-box { background: #fff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); }
  .bk-mod-right-box h4 { font-size: 1.05rem; font-weight: 700; color: #0f172a; margin-bottom: 18px; }
  .bk-mod-right-row { display: flex; align-items: center; justify-content: space-between; padding: 13px 0; border-bottom: 1px solid #f1f5f9; }
  .bk-mod-right-row:last-child { border-bottom: none; }
  .bk-mod-right-label { font-size: 13px; color: #475569; font-weight: 500; display: flex; align-items: center; gap: 8px; }
  .bk-mod-right-val { font-size: 13px; font-weight: 700; color: #2563eb; }
  .bk-mod-cards { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 18px; margin-bottom: 48px; }
  @media (max-width: 900px) { .bk-mod-cards { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .bk-mod-cards { grid-template-columns: 1fr; } }
  .bk-mod-card { background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; padding: 24px 22px; box-shadow: 0 1px 3px rgba(15,23,42,0.04); transition: all 0.2s; }
  .bk-mod-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(15,23,42,0.09); border-color: #93c5fd; }
  .bk-mod-card-chip { font-size: 11px; font-weight: 700; color: #2563eb; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 7px; }
  .bk-mod-card h4 { font-size: 0.98rem; font-weight: 700; color: #0f172a; margin-bottom: 8px; }
  .bk-mod-card p { font-size: 0.87rem; color: #64748b; line-height: 1.65; }
  .bk-mod-scenarios { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px; }
  @media (max-width: 900px) { .bk-mod-scenarios { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .bk-mod-scenarios { grid-template-columns: 1fr; } }
  .bk-mod-scenario { background: #0f172a; border-radius: 18px; padding: 22px; color: #e5e7eb; box-shadow: 0 10px 28px rgba(15,23,42,0.45); transition: transform 0.18s; }
  .bk-mod-scenario:hover { transform: translateY(-2px) scale(1.01); }
  .bk-mod-scenario-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #93c5fd; margin-bottom: 6px; }
  .bk-mod-scenario h4 { font-size: 0.97rem; font-weight: 700; margin-bottom: 7px; color: #f1f5f9; }
  .bk-mod-scenario p { font-size: 0.85rem; color: #94a3b8; line-height: 1.62; }
  .bk-mod-faq { border-top: 1px solid #e2e8f0; padding-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
  @media (max-width: 900px) { .bk-mod-faq { grid-template-columns: 1fr; } }
  .bk-mod-faq-item h4 { font-size: 0.96rem; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
  .bk-mod-faq-item p { font-size: 0.88rem; color: #64748b; line-height: 1.65; }
  .bk-mod-cta { background: #0f172a; border-radius: 24px; padding: 56px 48px; text-align: center; position: relative; overflow: hidden; margin-top: 64px; }
  @media (max-width: 600px) { .bk-mod-cta { padding: 40px 24px; } }
  .bk-mod-cta-glow { position: absolute; top: -20%; right: -5%; width: 360px; height: 360px; background: radial-gradient(circle, rgba(37,99,235,0.3), transparent 65%); pointer-events: none; }
  .bk-mod-cta h2 { font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; margin-bottom: 14px; position: relative; z-index: 1; }
  .bk-mod-cta p { font-size: 1rem; color: #94a3b8; max-width: 480px; margin: 0 auto 32px; line-height: 1.75; position: relative; z-index: 1; }
  .bk-mod-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .bk-mod-cta-primary { padding: 13px 28px; background: #2563eb; color: #fff; border: none; border-radius: 11px; font-size: 15px; font-weight: 700; cursor: pointer; transition: background 0.2s; }
  .bk-mod-cta-primary:hover { background: #3b82f6; }
  .bk-mod-cta-secondary { padding: 13px 28px; background: #1e293b; color: #cbd5e1; border: 1px solid #334155; border-radius: 11px; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .bk-mod-cta-secondary:hover { background: #334155; }
`;

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.6 },
};

const services = [
  { color: 'c-blue',    icon: '🧾', slug: 'daily-bookkeeping',    title: 'Daily Bookkeeping',               description: 'We record every transaction as it happens — sales, expenses, transfers, and adjustments — keeping your ledger accurate, current, and audit-ready at all times.', tags: ['Transaction Recording', 'Ledger Maintenance', 'Daily Sync'] },
  { color: 'c-indigo',  icon: '📊', slug: 'financial-reporting',  title: 'Financial Reporting',             description: 'Get clean, professional financial statements every month — P&L, balance sheet, and cash flow reports — formatted exactly the way banks, investors, and the IRS expect.', tags: ['P&L Statements', 'Balance Sheet', 'Cash Flow'] },
  { color: 'c-violet',  icon: '💳', slug: 'accounts-management',  title: 'Accounts Payable & Receivable',   description: 'Never miss a payment or let an invoice slip through the cracks. We track every bill and receivable, manage aging reports, and keep your cash flow moving in the right direction.', tags: ['Invoice Tracking', 'Aging Reports', 'Payment Alerts'] },
  { color: 'c-teal',    icon: '🏦', slug: 'bank-reconciliation',  title: 'Bank Reconciliation',             description: 'We match every transaction in your books to your bank and credit card statements monthly — catching discrepancies, detecting errors, and ensuring your records are always spotless.', tags: ['Monthly Reconciliation', 'Error Detection', 'Multi-Account'] },
  { color: 'c-sky',     icon: '📋', slug: 'expense-tracking',     title: 'Expense Tracking & Management',   description: 'Categorize, track, and analyze every business expense with precision. Identify overspend, maximize deductible costs, and gain full visibility into where your money is going.', tags: ['Expense Categorization', 'Budget Tracking', 'Tax Deductions'] },
  { color: 'c-emerald', icon: '📁', slug: 'tax-ready-books',      title: 'Tax-Ready Financials',            description: "Year-round bookkeeping structured to meet your accountant's exact requirements. When tax season arrives, your books are already clean, organized, and ready to file.", tags: ['Year-Round Prep', 'CPA-Ready Reports', 'Deduction Optimization'] },
];

const trustItems = [
  { icon: '🎯', title: '99.8% Accuracy Rate',           description: 'Our double-entry bookkeeping process and multi-point review system ensures your records are virtually error-free — every single month.' },
  { icon: '🔒', title: 'Bank-Level Data Security',      description: '256-bit encryption, SOC 2 Type II certification, and role-based access controls protect your most sensitive financial information around the clock.' },
  { icon: '⚡', title: '48-Hour Turnaround',            description: 'Monthly financial statements delivered within 48 hours of month-end close. No waiting weeks for reports you need to make business decisions.' },
  { icon: '📞', title: 'Dedicated Bookkeeper',          description: 'You work with one assigned bookkeeper who knows your business inside and out — available by email or phone whenever you have a question.' },
  { icon: '🔄', title: 'Seamless Software Integration', description: 'We work directly inside your existing accounting software — QuickBooks, Xero, Wave, FreshBooks — with zero disruption to your current workflow.' },
  { icon: '📆', title: 'IRS Audit Support',             description: "If you're ever audited, we're in your corner. Our clean records and organized documentation make audit response straightforward and stress-free." },
];

const testimonials = [
  { color: '#2563eb', initials: 'DM', name: 'Daniel Morris',   role: 'Owner, Morris & Sons Contracting', text: '"Before BizServe, I had no idea if my business was actually profitable. Now I get a clean P&L every month and I can see exactly where the money is going. It\'s completely changed how I run the business."' },
  { color: '#7c3aed', initials: 'LP', name: 'Laura Park',      role: 'CEO, Bloom Studio Agency',         text: '"Tax season used to be a nightmare. Now my CPA spends 30 minutes reviewing books that are already spotless. The time and money we\'ve saved is remarkable. I can\'t imagine going back."' },
  { color: '#0d9488', initials: 'RS', name: 'Ricardo Salazar', role: 'Founder, Salazar Logistics LLC',   text: '"I was behind on my books by four months when I came to BizServe. They caught up everything, found deductions my old accountant missed, and now I\'m completely current. Worth every dollar."' },
];

/* ─────────────────────────────────────────
   SERVICE SUB-PAGES
───────────────────────────────────────── */
interface ServicePageProps { onBack: () => void; }

/* ── 1. Daily Bookkeeping ── */
const DailyBookkeepingPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="bk-svchero">
      <div className="bk-svchero-blob1" />
      <div className="bk-svchero-blob2" />
      <div className="bk-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="bk-back-btn" onClick={onBack}>← Back to Bookkeeping</button>
          <div className="bk-mod-kicker">Core Service</div>
          <h1 className="bk-mod-title">Your Books, Updated Every Single Day.</h1>
          <p className="bk-hero-sub">
            Manual bookkeeping leads to backlogs, errors, and financial blind spots. Our team records
            every transaction as it occurs — so your ledger is always current, your accounts are always
            balanced, and you always know exactly where your business stands.
          </p>
          <div className="bk-mod-tags" style={{ marginBottom: '36px' }}>
            {['Transaction Entry', 'Account Coding', 'Bank Feed Sync', 'Ledger Maintenance', 'Daily Categorization', 'Error Detection'].map((t, i) => <span key={i} className="bk-mod-tag">{t}</span>)}
          </div>
          <div className="bk-btn-group">
            <button className="bk-btn-primary">Get started today →</button>
            <button className="bk-btn-secondary">Talk to a bookkeeper</button>
          </div>
        </motion.div>
        <motion.div className="bk-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="bk-svchero-card-hdr">
            <div><div className="bk-svchero-hdr-text">Today's Bookkeeping Activity</div><div className="bk-svchero-hdr-sub">Live ledger status</div></div>
            <div className="bk-svchero-hdr-icon">🧾</div>
          </div>
          {[
            { icon: '✅', label: 'Transactions Recorded',  val: '47 entries' },
            { icon: '🔗', label: 'Bank Feeds Synced',      val: '3 accounts' },
            { icon: '❓', label: 'Uncategorized Items',     val: '0 remaining' },
            { icon: '🕐', label: 'Last Ledger Check',      val: '9:32 AM today' },
            { icon: '⚠️', label: 'Data Entry Errors',      val: 'None detected' },
          ].map((r, i) => (
            <div key={i} className="bk-svchero-row">
              <span className="bk-svchero-row-label">{r.icon} {r.label}</span>
              <span className="bk-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="bk-svchero-metric">
            <div><div className="bk-svchero-metric-label">Books Status</div><div className="bk-svchero-metric-value">Up to date</div></div>
            <div className="bk-svchero-metric-badge">✓ Current as of today</div>
          </div>
        </motion.div>
      </div>
    </section>

  <section className="bk-mod">
    <div className="bk-mod-inner">

      <div className="bk-mod-split">
        <div>
          <div className="bk-mod-section">
            <h3>A ledger that reflects reality — every day</h3>
            <p className="bk-mod-split-body">
              Most small businesses review their books once a month — or once a quarter — which means
              decisions are made on stale data. BizServe bookkeepers work in your books daily, so your
              financial picture is always accurate and always current.
            </p>
            <div className="bk-mod-check-list">
              {[
                'All income, expenses, transfers, and refunds recorded within 24 hours',
                'Chart of accounts organized specifically to your business structure',
                'Bank and credit card feeds imported and matched to transactions automatically',
                'Multi-currency support for businesses with international sales',
                'Duplicate and unclear transactions flagged and resolved immediately',
                'Daily balance verification to catch discrepancies before they compound',
              ].map((item, i) => (
                <div key={i} className="bk-mod-check">
                  <span className="bk-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="bk-mod-right-box">
            <h4>Today's Bookkeeping Activity</h4>
            {[
              { icon: '✅', label: 'Transactions Recorded',    val: '47 entries' },
              { icon: '🔗', label: 'Bank Feeds Synced',        val: '3 accounts' },
              { icon: '❓', label: 'Uncategorized Items',       val: '0 remaining' },
              { icon: '🕐', label: 'Last Ledger Check',        val: '9:32 AM today' },
              { icon: '⚠️', label: 'Data Entry Errors',        val: 'None detected' },
              { icon: '📚', label: 'Books Status',             val: 'Up to date ✓' },
            ].map((r, i) => (
              <div key={i} className="bk-mod-right-row">
                <span className="bk-mod-right-label">{r.icon} {r.label}</span>
                <span className="bk-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>What daily bookkeeping covers</h3>
        <div className="bk-mod-cards">
          {[
            { chip: 'Transaction Entry',  title: 'Real-Time Recording',          body: 'Every sale, purchase, transfer, and adjustment is entered as it happens. No month-end data dumps — your books reflect reality in real time.' },
            { chip: 'Account Coding',     title: 'Correct Categorization',        body: 'Every transaction is assigned to the right account using your chart of accounts, ensuring your reports are accurate and your tax deductions are defensible.' },
            { chip: 'Bank Feed Sync',     title: 'Automated Feed Integration',    body: 'We connect directly to your bank and credit card feeds to import transactions automatically, eliminating manual entry and reducing human error.' },
            { chip: 'Multi-Entity',       title: 'Multiple Entity Support',       body: 'Running multiple business entities or locations? We maintain separate, clean books for each — with consolidated reporting available on request.' },
            { chip: 'Receipts',           title: 'Receipt Matching & Storage',    body: 'Submit receipts via email, app, or upload. We match them to transactions, store them securely, and ensure every expense is properly documented.' },
            { chip: 'Exception Flags',    title: 'Proactive Issue Detection',     body: 'Unusual transactions, duplicates, or unclear items are flagged immediately and resolved with you — before they create downstream reconciliation problems.' },
          ].map((c, i) => (
            <div key={i} className="bk-mod-card">
              <div className="bk-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="bk-mod-scenarios">
          {[
            { label: 'High Volume',   title: 'Retail rush — 2,000 transactions, zero backlog',   body: 'A Shopify retailer processes 80+ orders a day during the holiday season. Every transaction is categorized and reconciled daily. December closes cleanly, on time, with no scramble.' },
            { label: 'Mixed Payments', title: 'Cash + digital payments, one clean ledger',       body: 'A food truck takes cash, Square, Venmo, and online catering deposits. BizServe consolidates every payment type into one accurate ledger — no double counting, no missing revenue.' },
            { label: 'Behind on Books', title: '4 months behind, caught up in 5 business days', body: 'A contractor came to BizServe with books untouched since January. We reconstructed four months of transactions from bank statements and invoices. They were current before month-end.' },
          ].map((s, i) => (
            <div key={i} className="bk-mod-scenario">
              <div className="bk-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="bk-mod-faq">
          {[
            { q: 'How often are transactions actually entered?',     a: 'Every business day, typically within 24 hours of your bank feed updating. For high-volume businesses we offer same-day entry to keep your books current at all times.' },
            { q: 'What accounting software do you work in?',        a: 'We work directly inside your existing software — QuickBooks Online, Xero, Wave, or FreshBooks. There is no migration required and no disruption to your current setup.' },
            { q: 'What happens if a transaction is unclear?',       a: 'We flag it and send a brief email question, usually one or two sentences. Most are resolved within one business day, and we keep a log of all decisions for your records.' },
            { q: 'Do you handle cash transactions?',                a: 'Yes. We record cash transactions from daily sales summaries, cash register totals, or petty cash logs you provide. We can design a simple submission process that fits your workflow.' },
          ].map((f, i) => (
            <div key={i} className="bk-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-cta">
        <div className="bk-mod-cta-glow" />
        <h2>Start with books that are always current.</h2>
        <p>Get your first month of daily bookkeeping and see the difference accurate records make.</p>
        <div className="bk-mod-cta-btns">
          <button className="bk-mod-cta-primary">Get started today →</button>
          <button className="bk-mod-cta-secondary">Schedule a free consultation</button>
        </div>
      </div>
    </div>
  </section>
  </>
);

/* ── 2. Financial Reporting ── */
const FinancialReportingPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="bk-svchero">
      <div className="bk-svchero-blob1" />
      <div className="bk-svchero-blob2" />
      <div className="bk-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="bk-back-btn" onClick={onBack}>← Back to Bookkeeping</button>
          <div className="bk-mod-kicker">Monthly Deliverables</div>
          <h1 className="bk-mod-title">Financial Statements That Actually Tell You Something.</h1>
          <p className="bk-hero-sub">
            A financial report is only valuable if you can read it, trust it, and act on it. BizServe
            delivers clean, professionally formatted statements every month — not just numbers, but the
            financial story of your business, explained in plain language.
          </p>
          <div className="bk-mod-tags" style={{ marginBottom: '36px' }}>
            {['Profit & Loss', 'Balance Sheet', 'Cash Flow Statement', 'Custom Reports', 'KPI Dashboards', 'Monthly Delivery'].map((t, i) => <span key={i} className="bk-mod-tag">{t}</span>)}
          </div>
          <div className="bk-btn-group">
            <button className="bk-btn-primary">Get started today →</button>
            <button className="bk-btn-secondary">Talk to a bookkeeper</button>
          </div>
        </motion.div>
        <motion.div className="bk-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="bk-svchero-card-hdr">
            <div><div className="bk-svchero-hdr-text">November 2025 — Reports</div><div className="bk-svchero-hdr-sub">Delivered within 48 hrs of close</div></div>
            <div className="bk-svchero-hdr-icon">📊</div>
          </div>
          {[
            { icon: '📊', label: 'Profit & Loss',         val: 'Delivered Nov 2' },
            { icon: '⚖️', label: 'Balance Sheet',         val: 'Delivered Nov 2' },
            { icon: '💧', label: 'Cash Flow Statement',   val: 'Delivered Nov 2' },
            { icon: '📈', label: 'Revenue vs Prior Year', val: '+18% growth' },
            { icon: '💰', label: 'Net Profit Margin',     val: '24.3%' },
          ].map((r, i) => (
            <div key={i} className="bk-svchero-row">
              <span className="bk-svchero-row-label">{r.icon} {r.label}</span>
              <span className="bk-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="bk-svchero-metric">
            <div><div className="bk-svchero-metric-label">Avg. Delivery Time</div><div className="bk-svchero-metric-value">48 hours</div></div>
            <div className="bk-svchero-metric-badge">After month-end close</div>
          </div>
        </motion.div>
      </div>
    </section>

  <section className="bk-mod">
    <div className="bk-mod-inner">

      <div className="bk-mod-split">
        <div>
          <div className="bk-mod-section">
            <h3>The three reports every business owner needs</h3>
            <p className="bk-mod-split-body">
              Most small business owners see their financial reports once a year — at tax time — when
              it's too late to act on the information. BizServe delivers your three core statements
              within 48 hours of every month-end, so you always have current data to make decisions.
            </p>
            <div className="bk-mod-check-list">
              {[
                'Profit & Loss statement showing revenue, expenses, and net profit by month',
                'Balance sheet with assets, liabilities, and owner equity as of month-end',
                'Cash flow statement distinguishing operating, investing, and financing activities',
                'Year-over-year and month-over-month comparison columns included by default',
                'Department or location breakdowns available for multi-unit businesses',
                'Plain-language summary email highlighting key trends and any items to watch',
              ].map((item, i) => (
                <div key={i} className="bk-mod-check">
                  <span className="bk-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="bk-mod-right-box">
            <h4>November 2025 — Reports Delivered</h4>
            {[
              { icon: '📊', label: 'Profit & Loss',          val: 'Delivered Nov 2' },
              { icon: '⚖️', label: 'Balance Sheet',          val: 'Delivered Nov 2' },
              { icon: '💧', label: 'Cash Flow Statement',    val: 'Delivered Nov 2' },
              { icon: '📈', label: 'Revenue vs. Prior Year', val: '+18% growth' },
              { icon: '💰', label: 'Net Profit Margin',      val: '24.3%' },
              { icon: '🎯', label: 'Top Expense Category',   val: 'Payroll (52%)' },
            ].map((r, i) => (
              <div key={i} className="bk-mod-right-row">
                <span className="bk-mod-right-label">{r.icon} {r.label}</span>
                <span className="bk-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Full reporting suite — delivered monthly</h3>
        <div className="bk-mod-cards">
          {[
            { chip: 'P&L',              title: 'Profit & Loss Statement',     body: 'See every revenue stream and expense category for the month, quarter, and year. Understand exactly what your business earned and where the money went.' },
            { chip: 'Balance Sheet',    title: 'Balance Sheet',               body: 'A snapshot of your business\'s financial health — what you own, what you owe, and what\'s left. Essential for loans, investors, and long-term planning.' },
            { chip: 'Cash Flow',        title: 'Cash Flow Statement',         body: 'Profit and cash are not the same thing. This report shows exactly when money enters and leaves your business — critical for avoiding cash crunches.' },
            { chip: 'Custom Reports',   title: 'Custom Report Builds',        body: 'Need sales by product line, profit by location, or expenses by project code? We build custom reports tailored to the decisions you actually need to make.' },
            { chip: 'KPI Tracking',     title: 'Key Financial KPIs',          body: 'Gross margin, net margin, burn rate, receivables aging, and more — tracked monthly so you always know which levers to pull.' },
            { chip: 'Year-End Package', title: 'Year-End Financial Package',  body: 'A complete set of annual financials formatted to your CPA\'s exact specifications, delivered in January — ready for tax preparation with zero scramble.' },
          ].map((c, i) => (
            <div key={i} className="bk-mod-card">
              <div className="bk-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="bk-mod-scenarios">
          {[
            { label: 'Bank Loan',     title: 'Loan approved in 48 hours',                 body: 'A restaurant owner applies for a $150,000 SBA loan. The bank requests 3 years of financials. BizServe delivers a complete, formatted package the next morning. Loan approved within the week.' },
            { label: 'Pricing Fix',  title: 'Discovered service line losing money',        body: 'Monthly P&L reveals one service package has a 6% margin — far below the 30% target. Owner reprices within 30 days. The following month, overall margin improves by 8 points.' },
            { label: 'Cash Crisis',  title: 'Avoided a cash shortfall — 6 weeks early',  body: 'Cash flow report shows a projected $40,000 gap in 6 weeks due to a large receivable delay. Owner accelerates invoicing and draws on a credit line. Crisis averted before it started.' },
          ].map((s, i) => (
            <div key={i} className="bk-mod-scenario">
              <div className="bk-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="bk-mod-faq">
          {[
            { q: 'When exactly are reports delivered each month?',         a: 'Within 48 hours of the last business day of the month. Most clients receive their reports on the 2nd or 3rd of the following month, every month without exception.' },
            { q: 'Can I request reports in a specific format?',            a: 'Yes. We deliver PDF, Excel, and in-platform reports. If your CPA or bank has a specific template or format they prefer, we will match it exactly.' },
            { q: 'Do you explain what the numbers mean?',                  a: 'Absolutely. Every monthly delivery includes a plain-language summary email highlighting notable changes, trends to watch, and any items that may require your attention.' },
            { q: 'Can I get more frequent reporting — weekly or bi-weekly?', a: 'Yes. For businesses that need more frequent visibility, we offer weekly P&L snapshots and real-time dashboard access as part of our premium reporting tier.' },
          ].map((f, i) => (
            <div key={i} className="bk-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-cta">
        <div className="bk-mod-cta-glow" />
        <h2>Know your numbers. Make better decisions.</h2>
        <p>Clean financial statements delivered to your inbox within 48 hours of every month-end.</p>
        <div className="bk-mod-cta-btns">
          <button className="bk-mod-cta-primary">Get started today →</button>
          <button className="bk-mod-cta-secondary">Schedule a free consultation</button>
        </div>
      </div>
    </div>
  </section>
  </>
);

/* ── 3. Accounts Payable & Receivable ── */
const AccountsManagementPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="bk-svchero">
      <div className="bk-svchero-blob1" />
      <div className="bk-svchero-blob2" />
      <div className="bk-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="bk-back-btn" onClick={onBack}>← Back to Bookkeeping</button>
          <div className="bk-mod-kicker">Cash Flow Management</div>
          <h1 className="bk-mod-title">Get Paid Faster. Pay Smarter.</h1>
          <p className="bk-hero-sub">
            Missed invoices and overlooked bills are silent killers of small business cash flow. BizServe
            tracks every dollar owed to you and every bill you owe — keeping your receivables current,
            your payables on schedule, and your cash position healthy at all times.
          </p>
          <div className="bk-mod-tags" style={{ marginBottom: '36px' }}>
            {['Invoice Tracking', 'Vendor Bills', 'Aging Reports', 'Payment Alerts', 'Collections Follow-Up', 'Cash Flow Visibility'].map((t, i) => <span key={i} className="bk-mod-tag">{t}</span>)}
          </div>
          <div className="bk-btn-group">
            <button className="bk-btn-primary">Get started today →</button>
            <button className="bk-btn-secondary">Talk to a bookkeeper</button>
          </div>
        </motion.div>
        <motion.div className="bk-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="bk-svchero-card-hdr">
            <div><div className="bk-svchero-hdr-text">AR / AP Snapshot</div><div className="bk-svchero-hdr-sub">This week's cash position</div></div>
            <div className="bk-svchero-hdr-icon">💳</div>
          </div>
          {[
            { icon: '💚', label: 'Current (0–30 days)',    val: '$28,400' },
            { icon: '🟡', label: 'Overdue (31–60 days)',  val: '$6,200' },
            { icon: '🔴', label: 'Past Due (60+ days)',    val: '$1,800' },
            { icon: '📤', label: 'Bills Due This Week',    val: '$4,320' },
            { icon: '📅', label: 'Bills Due Next 30 Days', val: '$12,800' },
          ].map((r, i) => (
            <div key={i} className="bk-svchero-row">
              <span className="bk-svchero-row-label">{r.icon} {r.label}</span>
              <span className="bk-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="bk-svchero-metric">
            <div><div className="bk-svchero-metric-label">Net Cash Position (30-day)</div><div className="bk-svchero-metric-value">+$17,480</div></div>
            <div className="bk-svchero-metric-badge">↑ Positive flow</div>
          </div>
        </motion.div>
      </div>
    </section>

  <section className="bk-mod">
    <div className="bk-mod-inner">

      <div className="bk-mod-split">
        <div>
          <div className="bk-mod-section">
            <h3>Nothing falls through the cracks</h3>
            <p className="bk-mod-split-body">
              The average small business has 7–12 open invoices and 4–8 unpaid vendor bills at any
              given time. Without a system, things slip. BizServe tracks every open item, sends
              aging reports, and keeps you aware of what needs attention before it becomes a problem.
            </p>
            <div className="bk-mod-check-list">
              {[
                'Every client invoice logged, tracked, and aged from the day it is issued',
                'Accounts receivable aging report delivered weekly — 0–30, 31–60, 60+ days',
                'All vendor bills recorded as soon as received, with due dates tracked',
                'Payment reminders and follow-up scheduling for overdue receivables',
                'Vendor payment schedule to avoid late fees and preserve supplier relationships',
                'Monthly cash flow projection based on expected inflows and outflows',
              ].map((item, i) => (
                <div key={i} className="bk-mod-check">
                  <span className="bk-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="bk-mod-right-box">
            <h4>AR / AP Snapshot — This Week</h4>
            {[
              { icon: '💚', label: 'Invoices Current (0–30 days)',  val: '$28,400' },
              { icon: '🟡', label: 'Invoices Overdue (31–60 days)', val: '$6,200' },
              { icon: '🔴', label: 'Invoices Past Due (60+ days)',  val: '$1,800' },
              { icon: '📤', label: 'Bills Due This Week',           val: '$4,320' },
              { icon: '📅', label: 'Bills Due Next 30 Days',        val: '$12,800' },
              { icon: '💰', label: 'Net Cash Position (30-day)',    val: '+$17,480' },
            ].map((r, i) => (
              <div key={i} className="bk-mod-right-row">
                <span className="bk-mod-right-label">{r.icon} {r.label}</span>
                <span className="bk-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Full AP/AR management — handled end to end</h3>
        <div className="bk-mod-cards">
          {[
            { chip: 'Receivables',      title: 'Invoice Tracking & Aging',      body: 'Every client invoice is entered, aged, and monitored. You receive a weekly AR aging report showing exactly what is current, what is late, and what needs a follow-up call.' },
            { chip: 'Payables',         title: 'Vendor Bill Management',        body: 'Every vendor bill is recorded when received, matched to the right expense account, and flagged with its due date — so you never miss a payment or pay a late fee.' },
            { chip: 'Payment Alerts',   title: 'Due Date & Overdue Alerts',     body: 'Automated alerts notify you when invoices are approaching due dates and when receivables go past 30, 60, or 90 days — so follow-up happens on time, every time.' },
            { chip: 'Collections',      title: 'Collections Support',           body: 'For overdue receivables, we help you track follow-up activity, document collection efforts, and — if needed — prepare records for collections escalation.' },
            { chip: 'Reconciliation',   title: 'AP/AR to GL Reconciliation',    body: 'We reconcile your accounts payable and receivable sub-ledgers to the general ledger monthly, ensuring your balance sheet always reflects the true state of your cash position.' },
            { chip: 'Cash Forecasting', title: '30-Day Cash Flow Projection',   body: 'Using open invoices and upcoming bills, we generate a rolling 30-day cash flow projection so you know exactly when cash gaps are coming — and have time to act.' },
          ].map((c, i) => (
            <div key={i} className="bk-mod-card">
              <div className="bk-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="bk-mod-scenarios">
          {[
            { label: 'Late Payments',  title: '$22,000 recovered in 45 days',           body: 'A marketing agency had four clients 60+ days overdue totaling $22,000. BizServe\'s aging report surfaced them. Structured follow-up emails and a call script recovered the full balance within 45 days.' },
            { label: 'Missed Bill',    title: 'Caught a $3,400 double payment',          body: 'A vendor invoiced twice for the same delivery. BizServe\'s bill tracking flagged the duplicate before payment was made. The vendor confirmed the error and issued a credit.' },
            { label: 'Cash Planning',  title: 'Prevented a $50,000 cash shortfall',      body: 'A 30-day projection showed a $50,000 gap in six weeks due to slow receivables. The owner accelerated collections and timed a large vendor payment to avoid the gap entirely.' },
          ].map((s, i) => (
            <div key={i} className="bk-mod-scenario">
              <div className="bk-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="bk-mod-faq">
          {[
            { q: 'Do you send invoices to clients on our behalf?',           a: 'We can enter and track invoices in your accounting software and flag overdue items, but the actual sending and client communication remains with you unless you specifically request a managed collections service.' },
            { q: 'How do you receive vendor bills?',                         a: 'You can forward bill emails to your dedicated BizServe inbox, upload PDFs to your shared folder, or connect your email directly. We process every bill typically within one business day of receipt.' },
            { q: 'Can you integrate with our invoicing software?',           a: 'Yes. We work with QuickBooks, Xero, FreshBooks, HoneyBook, and most major invoicing tools. Bills and invoices sync automatically with no manual re-entry on your end.' },
            { q: 'What if a client disputes an invoice?',                    a: 'We maintain a complete record of every invoice, amount, date, and any adjustments made — giving you a clean paper trail to resolve disputes quickly and professionally.' },
          ].map((f, i) => (
            <div key={i} className="bk-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-cta">
        <div className="bk-mod-cta-glow" />
        <h2>Stop chasing invoices. Start managing cash flow.</h2>
        <p>Know exactly what you are owed and what you owe — every single week.</p>
        <div className="bk-mod-cta-btns">
          <button className="bk-mod-cta-primary">Get started today →</button>
          <button className="bk-mod-cta-secondary">Schedule a free consultation</button>
        </div>
      </div>
    </div>
  </section>
  </>
);

/* ── 4. Bank Reconciliation ── */
const BankReconciliationPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="bk-svchero">
      <div className="bk-svchero-blob1" />
      <div className="bk-svchero-blob2" />
      <div className="bk-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="bk-back-btn" onClick={onBack}>← Back to Bookkeeping</button>
          <div className="bk-mod-kicker">Accuracy &amp; Verification</div>
          <h1 className="bk-mod-title">Every Cent Accounted For.</h1>
          <p className="bk-hero-sub">
            Bank reconciliation is the process that catches everything else — errors, fraud, missed
            transactions, and software glitches. BizServe reconciles every bank account and credit
            card monthly, ensuring your books always match reality and your records are defensible.
          </p>
          <div className="bk-mod-tags" style={{ marginBottom: '36px' }}>
            {['Monthly Reconciliation', 'Multi-Account', 'Discrepancy Resolution', 'Fraud Detection', 'Credit Card Matching', 'Clean Audit Trail'].map((t, i) => <span key={i} className="bk-mod-tag">{t}</span>)}
          </div>
          <div className="bk-btn-group">
            <button className="bk-btn-primary">Get started today →</button>
            <button className="bk-btn-secondary">Talk to a bookkeeper</button>
          </div>
        </motion.div>
        <motion.div className="bk-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="bk-svchero-card-hdr">
            <div><div className="bk-svchero-hdr-text">November Month-End</div><div className="bk-svchero-hdr-sub">All accounts reconciled</div></div>
            <div className="bk-svchero-hdr-icon">🏦</div>
          </div>
          {[
            { icon: '🏦', label: 'Checking Account',     val: 'Reconciled ✓' },
            { icon: '🏦', label: 'Savings Account',      val: 'Reconciled ✓' },
            { icon: '💳', label: 'Business Visa',        val: 'Reconciled ✓' },
            { icon: '💳', label: 'Business Amex',        val: 'Reconciled ✓' },
            { icon: '📱', label: 'Stripe Processor',     val: 'Reconciled ✓' },
          ].map((r, i) => (
            <div key={i} className="bk-svchero-row">
              <span className="bk-svchero-row-label">{r.icon} {r.label}</span>
              <span className="bk-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="bk-svchero-metric">
            <div><div className="bk-svchero-metric-label">Discrepancies Found</div><div className="bk-svchero-metric-value">0 this month</div></div>
            <div className="bk-svchero-metric-badge">✓ Zero variance</div>
          </div>
        </motion.div>
      </div>
    </section>

  <section className="bk-mod">
    <div className="bk-mod-inner">

      <div className="bk-mod-split">
        <div>
          <div className="bk-mod-section">
            <h3>The safeguard your books can't run without</h3>
            <p className="bk-mod-split-body">
              Reconciliation is not optional — it is the financial control that proves your books are
              accurate. Without it, errors go undetected, fraud goes unnoticed, and your financial
              statements can't be trusted. BizServe closes the loop on every account, every month.
            </p>
            <div className="bk-mod-check-list">
              {[
                'All checking, savings, and operating accounts reconciled each month-end',
                'Every business credit card matched transaction by transaction',
                'PayPal, Stripe, Square, and payment processor accounts reconciled separately',
                'All discrepancies investigated, explained, and resolved with documentation',
                'Cleared and outstanding items flagged on the month-end reconciliation report',
                'Complete reconciliation workpaper retained for every account, every month',
              ].map((item, i) => (
                <div key={i} className="bk-mod-check">
                  <span className="bk-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="bk-mod-right-box">
            <h4>November Month-End Reconciliation</h4>
            {[
              { icon: '🏦', label: 'Accounts Reconciled',         val: '5 of 5' },
              { icon: '💳', label: 'Credit Cards Matched',        val: '3 of 3' },
              { icon: '📱', label: 'Payment Processors',          val: 'Stripe + Square' },
              { icon: '⚠️', label: 'Discrepancies Found',         val: '2 resolved' },
              { icon: '🔍', label: 'Uncleared Transactions',      val: '1 outstanding' },
              { icon: '✅', label: 'Reconciliation Status',       val: 'Complete ✓' },
            ].map((r, i) => (
              <div key={i} className="bk-mod-right-row">
                <span className="bk-mod-right-label">{r.icon} {r.label}</span>
                <span className="bk-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Complete reconciliation coverage</h3>
        <div className="bk-mod-cards">
          {[
            { chip: 'Bank Accounts',     title: 'Checking & Savings Reconciliation',    body: 'Every bank account is reconciled monthly — matching every deposit, withdrawal, and transfer to your general ledger with a zero-variance sign-off.' },
            { chip: 'Credit Cards',      title: 'Business Credit Card Matching',        body: 'Every credit card transaction is matched to a receipt and categorized. Monthly statements are reconciled to eliminate ghost charges and ensure accurate reporting.' },
            { chip: 'Payment Processors', title: 'Stripe, Square & PayPal',            body: 'Processor settlements, fees, refunds, and chargebacks are reconciled against your bank deposits — ensuring gross revenue, net deposits, and fees are all correctly recorded.' },
            { chip: 'Discrepancies',     title: 'Discrepancy Investigation',            body: 'When something doesn\'t match, we investigate. Bank errors, duplicate entries, timing differences, and potential fraud are identified, documented, and resolved.' },
            { chip: 'Workpapers',        title: 'Reconciliation Workpapers',            body: 'A signed-off reconciliation workpaper is retained for every account and every month — giving you a complete audit trail that satisfies any lender, auditor, or tax authority.' },
            { chip: 'Fraud Detection',   title: 'Unauthorized Transaction Alerts',      body: 'Reconciliation is your primary fraud control. We flag transactions that appear unauthorized, inconsistent with normal patterns, or that lack proper documentation.' },
          ].map((c, i) => (
            <div key={i} className="bk-mod-card">
              <div className="bk-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="bk-mod-scenarios">
          {[
            { label: 'Fraud Caught',     title: '$4,800 in unauthorized charges detected', body: 'Reconciliation revealed 12 small charges from an unfamiliar merchant over three months totaling $4,800. The business owner contacted the bank, confirmed the fraud, and recovered the full amount.' },
            { label: 'Bank Error',       title: 'Bank credited wrong account — caught',    body: 'A $12,000 wire transfer was incorrectly credited to another account. BizServe\'s reconciliation caught the discrepancy on day three. The bank corrected the error before month-end.' },
            { label: 'Audit Readiness', title: 'IRS audit resolved in 2 hours',           body: 'An IRS notice requested substantiation for $38,000 in deductions. Every expense had a matched receipt and was documented in the monthly reconciliation workpapers. The response was filed the same day.' },
          ].map((s, i) => (
            <div key={i} className="bk-mod-scenario">
              <div className="bk-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="bk-mod-faq">
          {[
            { q: 'How many accounts can you reconcile?',                      a: 'We reconcile all of them — checking, savings, money market, every business credit card, and all payment processor accounts. There is no per-account surcharge for standard reconciliation.' },
            { q: 'What if my books haven\'t been reconciled for months?',     a: 'We perform a historical catch-up reconciliation to bring all accounts current. Most catch-ups covering 3–6 months are completed within one week.' },
            { q: 'Do I need to do anything during reconciliation?',           a: 'Very little. Occasionally we ask you to confirm an unusual transaction or provide a receipt for a flagged item. Most reconciliations are completed with no input from you at all.' },
            { q: 'Will you catch errors my previous bookkeeper made?',        a: 'Yes. When we begin with a new client we review prior-period reconciliations and often surface errors, misclassifications, or missing transactions that need correction.' },
          ].map((f, i) => (
            <div key={i} className="bk-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-cta">
        <div className="bk-mod-cta-glow" />
        <h2>Books that match your bank — guaranteed.</h2>
        <p>Monthly reconciliation that catches errors, prevents fraud, and gives you total confidence in your numbers.</p>
        <div className="bk-mod-cta-btns">
          <button className="bk-mod-cta-primary">Get started today →</button>
          <button className="bk-mod-cta-secondary">Schedule a free consultation</button>
        </div>
      </div>
    </div>
  </section>
  </>
);

/* ── 5. Expense Tracking ── */
const ExpenseTrackingPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="bk-svchero">
      <div className="bk-svchero-blob1" />
      <div className="bk-svchero-blob2" />
      <div className="bk-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="bk-back-btn" onClick={onBack}>← Back to Bookkeeping</button>
          <div className="bk-mod-kicker">Expense Management</div>
          <h1 className="bk-mod-title">See Exactly Where Every Dollar Goes.</h1>
          <p className="bk-hero-sub">
            Untracked expenses are money left on the table — unclaimed deductions, undetected overspend,
            and budget decisions based on guesswork. BizServe categorizes every dollar you spend with
            precision, giving you the full picture and every deduction you are entitled to.
          </p>
          <div className="bk-mod-tags" style={{ marginBottom: '36px' }}>
            {['Expense Categorization', 'Receipt Management', 'Budget Tracking', 'Tax Deduction Optimization', 'Vendor Analysis', 'Spend Reports'].map((t, i) => <span key={i} className="bk-mod-tag">{t}</span>)}
          </div>
          <div className="bk-btn-group">
            <button className="bk-btn-primary">Get started today →</button>
            <button className="bk-btn-secondary">Talk to a bookkeeper</button>
          </div>
        </motion.div>
        <motion.div className="bk-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="bk-svchero-card-hdr">
            <div><div className="bk-svchero-hdr-text">November Expense Breakdown</div><div className="bk-svchero-hdr-sub">By category — all deductible</div></div>
            <div className="bk-svchero-hdr-icon">📋</div>
          </div>
          <div className="bk-svchero-insight-title">Spend by Category</div>
          {[
            { label: 'Payroll',     pct: 52, color: '#2563eb', amt: '$28,400' },
            { label: 'Rent',        pct: 8,  color: '#4f46e5', amt: '$4,200'  },
            { label: 'Marketing',   pct: 6,  color: '#7c3aed', amt: '$3,100'  },
            { label: 'Software',    pct: 3,  color: '#0d9488', amt: '$1,840'  },
            { label: 'Travel',      pct: 2,  color: '#0284c7', amt: '$920'    },
          ].map((bar, i) => (
            <div key={i} className="bk-svchero-bar-row">
              <span className="bk-svchero-bar-label">{bar.label}</span>
              <div className="bk-svchero-bar-track">
                <div className="bk-svchero-bar-fill" style={{ width: `${bar.pct * 1.8}%`, background: bar.color }} />
              </div>
              <span className="bk-svchero-bar-val">{bar.amt}</span>
            </div>
          ))}
          <div className="bk-svchero-metric">
            <div><div className="bk-svchero-metric-label">Total Deductible Expenses</div><div className="bk-svchero-metric-value">$38,460</div></div>
            <div className="bk-svchero-metric-badge">100% documented</div>
          </div>
        </motion.div>
      </div>
    </section>

  <section className="bk-mod">
    <div className="bk-mod-inner">

      <div className="bk-mod-split">
        <div>
          <div className="bk-mod-section">
            <h3>Every expense, in the right bucket, every time</h3>
            <p className="bk-mod-split-body">
              The difference between a $40,000 tax bill and a $32,000 tax bill is often just
              proper expense categorization. Our bookkeepers ensure every expense is assigned to
              the right account, documented with a receipt, and optimized for maximum deductibility.
            </p>
            <div className="bk-mod-check-list">
              {[
                'Every expense coded to the correct chart of accounts category, consistently',
                'IRS-defensible categorization aligned with Schedule C and business tax codes',
                'Receipts collected, matched to transactions, and stored with permanent retention',
                'Mixed personal/business expenses flagged and owner draw adjustments made correctly',
                'Monthly expense breakdown report by category, vendor, and project',
                'Year-end deduction summary delivered to your CPA in their preferred format',
              ].map((item, i) => (
                <div key={i} className="bk-mod-check">
                  <span className="bk-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="bk-mod-right-box">
            <h4>November Expense Breakdown</h4>
            {[
              { icon: '👥', label: 'Payroll & Contractors',     val: '$28,400 (52%)' },
              { icon: '🏢', label: 'Rent & Facilities',         val: '$4,200 (8%)' },
              { icon: '📢', label: 'Marketing & Advertising',   val: '$3,100 (6%)' },
              { icon: '🛠️', label: 'Software & Subscriptions',  val: '$1,840 (3%)' },
              { icon: '🚗', label: 'Travel & Meals',            val: '$920 (2%)' },
              { icon: '📋', label: 'Deductible Total',          val: '$38,460 ✓' },
            ].map((r, i) => (
              <div key={i} className="bk-mod-right-row">
                <span className="bk-mod-right-label">{r.icon} {r.label}</span>
                <span className="bk-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Complete expense management, end to end</h3>
        <div className="bk-mod-cards">
          {[
            { chip: 'Categorization',   title: 'Precise Expense Coding',           body: 'Every transaction is assigned to the correct expense account using your chart of accounts — consistently, accurately, and in alignment with IRS guidelines for maximum deductibility.' },
            { chip: 'Receipts',         title: 'Digital Receipt Management',        body: 'Email, app, or upload your receipts. We match each one to the correct transaction, store it securely in the cloud, and ensure nothing is missing when tax time arrives.' },
            { chip: 'Deductions',       title: 'Tax Deduction Optimization',        body: 'Home office, vehicle use, meals, travel, equipment, and professional fees — we ensure every legitimate business expense is correctly categorized and documented for your CPA.' },
            { chip: 'Budget Tracking',  title: 'Budget vs. Actual Reporting',       body: 'Set monthly or quarterly expense budgets by category. Receive a monthly budget vs. actual report so you can see where you are over or under — and make adjustments before it is too late.' },
            { chip: 'Vendor Analysis',  title: 'Vendor Spend Reports',              body: 'See exactly how much you spend with each vendor across any time period. Identify subscription creep, renegotiate contracts, and consolidate duplicate services.' },
            { chip: 'Mixed Expenses',   title: 'Personal / Business Separation',    body: 'For sole proprietors and single-member LLCs, we flag personal charges that need owner draw treatment and ensure only legitimate business expenses appear on your financial statements.' },
          ].map((c, i) => (
            <div key={i} className="bk-mod-card">
              <div className="bk-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="bk-mod-scenarios">
          {[
            { label: 'Missed Deductions', title: '$14,000 in unclaimed deductions found',  body: 'A freelance consultant\'s prior-year books had vehicle expenses, home office, and software subscriptions recorded under a generic "Other" category. BizServe recategorized them correctly, saving $14,000 on the amended return.' },
            { label: 'Subscription Audit', title: 'Found 11 unused subscriptions',        body: 'Vendor spend report revealed $2,800/month in software subscriptions, including 11 that nobody on the team recognized. Canceling them saved $33,600 per year.' },
            { label: 'Budget Overage',     title: 'Marketing overspend caught in month 2', body: 'A budget vs. actual report in February showed marketing at 180% of budget. The owner paused two ad campaigns. By March, the category was back on plan and annual budget stayed intact.' },
          ].map((s, i) => (
            <div key={i} className="bk-mod-scenario">
              <div className="bk-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="bk-mod-faq">
          {[
            { q: 'What is the easiest way to submit receipts?',              a: 'Most clients forward receipts by email to their dedicated BizServe inbox. We also support mobile app upload, Dropbox, Google Drive folder sync, and direct integrations with Expensify and Dext.' },
            { q: 'What if I mix personal and business expenses?',            a: 'It happens. We flag the transaction, ask you to confirm, and handle it correctly — either as an owner draw, personal reimbursement, or legitimate business expense depending on what it is.' },
            { q: 'How far back can you find missed deductions?',             a: 'Tax returns can be amended for up to 3 years. If we identify significant missed deductions in prior-year books, we will flag them so your CPA can evaluate whether an amended return is worth filing.' },
            { q: 'Can you help us set expense budgets?',                     a: 'Yes. As part of onboarding, we will review your historical spending and help you set realistic monthly budgets by category. We track actuals against those budgets every month going forward.' },
          ].map((f, i) => (
            <div key={i} className="bk-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-cta">
        <div className="bk-mod-cta-glow" />
        <h2>Stop leaving deductions on the table.</h2>
        <p>Properly categorized expenses and maximum deductibility — every month, automatically.</p>
        <div className="bk-mod-cta-btns">
          <button className="bk-mod-cta-primary">Get started today →</button>
          <button className="bk-mod-cta-secondary">Schedule a free consultation</button>
        </div>
      </div>
    </div>
  </section>
  </>
);

/* ── 6. Tax-Ready Financials ── */
const TaxReadyBooksPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="bk-svchero">
      <div className="bk-svchero-blob1" />
      <div className="bk-svchero-blob2" />
      <div className="bk-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="bk-back-btn" onClick={onBack}>← Back to Bookkeeping</button>
          <div className="bk-mod-kicker">Tax Preparation</div>
          <h1 className="bk-mod-title">Tax Season Without the Panic.</h1>
          <p className="bk-hero-sub">
            For most small business owners, tax season means weeks of frantic paperwork, a frustrated
            accountant, and a bigger bill than expected. BizServe eliminates that entirely — with
            year-round bookkeeping structured precisely the way your CPA needs it, every single year.
          </p>
          <div className="bk-mod-tags" style={{ marginBottom: '36px' }}>
            {['Year-Round Tax Prep', 'CPA-Ready Financials', 'Deduction Maximization', 'Schedule C & S-Corp', 'Year-End Package', 'Amended Return Support'].map((t, i) => <span key={i} className="bk-mod-tag">{t}</span>)}
          </div>
          <div className="bk-btn-group">
            <button className="bk-btn-primary">Get started today →</button>
            <button className="bk-btn-secondary">Talk to a bookkeeper</button>
          </div>
        </motion.div>
        <motion.div className="bk-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="bk-svchero-card-hdr">
            <div><div className="bk-svchero-hdr-text">Year-End Tax Package</div><div className="bk-svchero-hdr-sub">Delivered January 3rd</div></div>
            <div className="bk-svchero-hdr-icon">📁</div>
          </div>
          {[
            { icon: '✅', label: 'Annual P&L Statement',      val: 'Delivered Jan 3' },
            { icon: '✅', label: 'Year-End Balance Sheet',    val: 'Delivered Jan 3' },
            { icon: '✅', label: 'Deduction Summary Report',  val: 'Delivered Jan 3' },
            { icon: '✅', label: '1099-NEC Prep',             val: '14 vendors filed' },
            { icon: '✅', label: 'Depreciation Schedule',     val: 'Updated ✓' },
          ].map((r, i) => (
            <div key={i} className="bk-svchero-row">
              <span className="bk-svchero-row-label">{r.icon} {r.label}</span>
              <span className="bk-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="bk-svchero-metric">
            <div><div className="bk-svchero-metric-label">Estimated Tax Savings</div><div className="bk-svchero-metric-value">$8,400</div></div>
            <div className="bk-svchero-metric-badge">vs prior year</div>
          </div>
        </motion.div>
      </div>
    </section>

  <section className="bk-mod">
    <div className="bk-mod-inner">

      <div className="bk-mod-split">
        <div>
          <div className="bk-mod-section">
            <h3>Tax season starts on January 1st — not April 1st</h3>
            <p className="bk-mod-split-body">
              A CPA's job is to file your taxes accurately and find every legal deduction. They
              can't do that if your books are a mess. BizServe maintains tax-ready books all year
              long — so when January arrives, your accountant gets organized financials and a
              complete deduction summary instead of a shoebox full of receipts.
            </p>
            <div className="bk-mod-check-list">
              {[
                'Chart of accounts structured to match your CPA\'s preferred reporting format',
                'Every deductible expense correctly categorized throughout the year',
                'Complete year-end financial package delivered in January, ready to file',
                'Depreciation schedules maintained and updated for all capitalized assets',
                'Contractor and vendor 1099 tracking with W-9 collection throughout the year',
                'Prior-year comparison reports included to support tax planning conversations',
              ].map((item, i) => (
                <div key={i} className="bk-mod-check">
                  <span className="bk-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="bk-mod-right-box">
            <h4>Year-End Tax Package — January Delivery</h4>
            {[
              { icon: '📊', label: 'Annual P&L Statement',         val: 'Delivered Jan 3' },
              { icon: '⚖️', label: 'Year-End Balance Sheet',       val: 'Delivered Jan 3' },
              { icon: '📋', label: 'Deduction Summary Report',     val: 'Delivered Jan 3' },
              { icon: '🧾', label: '1099-NEC Prep (Contractors)',   val: '14 vendors filed' },
              { icon: '🏠', label: 'Depreciation Schedule',        val: 'Updated ✓' },
              { icon: '💰', label: 'Estimated Tax Savings',        val: '$8,400 vs. prior yr' },
            ].map((r, i) => (
              <div key={i} className="bk-mod-right-row">
                <span className="bk-mod-right-label">{r.icon} {r.label}</span>
                <span className="bk-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Everything your CPA needs — organized and ready</h3>
        <div className="bk-mod-cards">
          {[
            { chip: 'Year-End Package',  title: 'Complete Year-End Financials',      body: 'A full set of annual financial statements — P&L, balance sheet, cash flow, and general ledger — formatted in your CPA\'s preferred format and delivered in the first week of January.' },
            { chip: 'Deduction Summary', title: 'Itemized Deduction Report',         body: 'A detailed breakdown of every deductible expense category — advertising, home office, vehicle, meals, travel, equipment, and more — with totals and documentation ready for Schedule C or your business return.' },
            { chip: '1099 Tracking',     title: '1099-NEC & Contractor Management', body: 'We track every contractor and vendor payment over $600 throughout the year, collect W-9s, and prepare all required 1099-NEC forms for filing by January 31.' },
            { chip: 'Depreciation',      title: 'Asset & Depreciation Schedules',   body: 'All capitalized assets are tracked with their cost, purchase date, useful life, and accumulated depreciation. Schedules are delivered to your CPA alongside the year-end package.' },
            { chip: 'Tax Planning',      title: 'Mid-Year Tax Projection',           body: 'In June or July, we prepare a mid-year estimated tax liability report so your CPA can advise on strategies — retirement contributions, equipment purchases, entity changes — before year-end.' },
            { chip: 'CPA Coordination',  title: 'Direct CPA Collaboration',         body: 'We work directly with your accountant. Share access to your books, answer questions, and coordinate delivery timelines so your CPA has everything they need, on time, every year.' },
          ].map((c, i) => (
            <div key={i} className="bk-mod-card">
              <div className="bk-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="bk-mod-scenarios">
          {[
            { label: 'Tax Savings',    title: '$22,000 tax bill reduced to $14,000',      body: 'A consulting firm\'s prior-year return had vehicle expenses, home office, and software miscategorized under "General Expenses." BizServe corrected the categorization and filed an amended return, producing an $8,000 refund.' },
            { label: 'Fast Filing',    title: 'Return filed February 15th — stress-free', body: 'A retail business with $1.4M in revenue had clean, CPA-ready books in the first week of January. Their accountant filed by February 15 — the earliest they had ever filed. CPA bill was 30% lower than the prior year.' },
            { label: '1099 Deadline',  title: 'All 1099s filed before January 31',        body: 'A marketing agency paid 22 freelancers during the year. BizServe tracked every payment, collected W-9s as contractors were onboarded, and had all 1099-NECs prepared and filed four days before the deadline.' },
          ].map((s, i) => (
            <div key={i} className="bk-mod-scenario">
              <div className="bk-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="bk-mod-faq">
          {[
            { q: 'Do you file our taxes directly?',                          a: 'No — we are bookkeepers, not tax preparers. We prepare and deliver everything your CPA needs to file. If you need a referral to a small business CPA, we are happy to connect you with one.' },
            { q: 'Will my accounting bill go down if I use BizServe?',       a: 'Almost always. CPAs typically charge between $150–$400/hr. When they receive clean, organized books instead of raw data, preparation time drops significantly — and so does your bill.' },
            { q: 'What if I\'ve been doing my own taxes in TurboTax?',       a: 'We\'ll have your books organized and compliant. Whether you continue using TurboTax or upgrade to a CPA, having accurate books makes the process faster and more accurate either way.' },
            { q: 'Can you help with prior years if my books are a mess?',    a: 'Yes. We perform historical cleanup for prior years — reconstructing transactions from bank statements, correcting miscategorizations, and preparing complete prior-year financial packages for your accountant.' },
          ].map((f, i) => (
            <div key={i} className="bk-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bk-mod-cta">
        <div className="bk-mod-cta-glow" />
        <h2>Never scramble at tax time again.</h2>
        <p>Year-round books that make tax season a non-event — for you and your accountant.</p>
        <div className="bk-mod-cta-btns">
          <button className="bk-mod-cta-primary">Get started today →</button>
          <button className="bk-mod-cta-secondary">Schedule a free consultation</button>
        </div>
      </div>
    </div>
  </section>
  </>
);

/* ─────────────────────────────────────────
   OVERVIEW PAGE
───────────────────────────────────────── */
interface OverviewProps { onSelectService: (slug: string) => void; }

const BookkeepingOverview: React.FC<OverviewProps> = ({ onSelectService }) => {
  return (
    <div className="bk-page">
      <style>{styles}</style>

      {/* ── Hero ── */}
      <section className="bk-hero">
        <div className="bk-hero-blob1" />
        <div className="bk-hero-blob2" />
        <div className="bk-hero-inner">
          <div>
            <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
              <div className="bk-badge">
                <span className="bk-badge-dot" />
                Trusted by 3,500+ small businesses — books that are always on time
              </div>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
              Books that close on time,{' '}
              <span className="bk-gradient-text">every month</span>.
            </motion.h1>

            <motion.p className="bk-hero-sub" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              BizServe handles all your bookkeeping so you can focus on growing your business.
              Accurate financial records, monthly reports, and a dedicated bookkeeper — without
              the overhead of a full-time hire.
            </motion.p>

            <motion.div className="bk-btn-group" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
              <button className="bk-btn-primary">Get started today →</button>
              <button className="bk-btn-secondary">See how it works</button>
            </motion.div>
          </div>

          <motion.div className="bk-hero-card" initial={{ opacity: 0, x: 44 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
            <div className="bk-card-header">
              <div>
                <div className="bk-card-title-label">November 2025</div>
                <div className="bk-card-title-value">Recent Transactions</div>
              </div>
              <div className="bk-card-icon">💼</div>
            </div>
            {[
              { icon: '🛒', bg: '#eff6ff', name: 'Shopify Sales Deposit',       date: 'Nov 28', amount: '+$4,820', cls: 'income'  },
              { icon: '🧰', bg: '#fef3c7', name: 'Vendor Invoice — Tools',      date: 'Nov 26', amount: '-$1,240', cls: 'expense' },
              { icon: '💡', bg: '#f0fdf4', name: 'Utility Bill — November',     date: 'Nov 24', amount: '-$318',   cls: 'expense' },
              { icon: '📦', bg: '#eef2ff', name: 'Client Payment — Invoice 108', date: 'Nov 22', amount: '+$6,500', cls: 'income' },
            ].map((tx, i) => (
              <div key={i} className="bk-tx-item">
                <div className="bk-tx-left">
                  <div className="bk-tx-icon" style={{ background: tx.bg }}>{tx.icon}</div>
                  <div>
                    <div className="bk-tx-name">{tx.name}</div>
                    <div className="bk-tx-date">{tx.date}</div>
                  </div>
                </div>
                <span className={`bk-tx-amount ${tx.cls}`}>{tx.amount}</span>
              </div>
            ))}
            <div className="bk-card-metric">
              <div>
                <div className="bk-card-metric-label">Net Profit — November</div>
                <div className="bk-card-metric-value">$18,340</div>
              </div>
              <div className="bk-card-metric-badge">↑ 22% vs October</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <div className="bk-stats">
        <div className="bk-stats-grid">
          {[
            { num: '3,500+', label: 'Small Businesses Served' },
            { num: '$2.5B+', label: 'Transactions Processed' },
            { num: '99.8%',  label: 'Bookkeeping Accuracy Rate' },
            { num: '48 hrs', label: 'Avg. Monthly Close Time' },
          ].map((s, i) => (
            <motion.div key={i} className="bk-stat-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
              <div className="bk-stat-num">{s.num}</div>
              <div className="bk-stat-label">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Services ── */}
      <section className="bk-section bk-services-bg">
        <div className="bk-container">
          <motion.div className="bk-section-header" {...fadeUp}>
            <div className="bk-kicker">Bookkeeping Services</div>
            <h2>Everything your books need, handled for you</h2>
            <p>From daily transaction recording to tax-ready financial statements, our bookkeepers manage every aspect of your small business finances with precision.</p>
          </motion.div>
          <div className="bk-services-grid">
            {services.map((svc, i) => (
              <motion.div
                key={i}
                className={`bk-svc-card ${svc.color}`}
                onClick={() => onSelectService(svc.slug)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
              >
                <div className={`bk-svc-icon ${svc.color}`}>{svc.icon}</div>
                <h3>{svc.title}</h3>
                <p>{svc.description}</p>
                <div className="bk-svc-tags">
                  {svc.tags.map((t, j) => <span key={j} className="bk-svc-tag">{t}</span>)}
                </div>
                <div className="bk-svc-arrow">Explore {svc.title} →</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature Spotlight ── */}
      <section className="bk-section bk-spotlight-bg">
        <div className="bk-container">
          <motion.div className="bk-spotlight-inner" {...fadeUp}>
            <div className="bk-spotlight-left">
              <div className="bk-kicker">Why BizServe Bookkeeping</div>
              <h2>Stop guessing at your finances. Start knowing.</h2>
              <p>Most small business owners are flying blind — managing finances in spreadsheets, scrambling at tax time, and making decisions without accurate data. BizServe gives you a clear financial picture every month.</p>
              <div className="bk-feat-list">
                {[
                  { icon: '📈', color: 'c-blue',   title: 'Real-Time Financial Clarity',   desc: "Always know your exact revenue, expenses, and profitability. No surprises at month-end — just clean, current numbers you can act on." },
                  { icon: '⏱️', color: 'c-indigo', title: 'Hours Saved Every Week',        desc: "Reclaim 10+ hours per week you're currently spending on data entry, chasing receipts, and manually reconciling accounts." },
                  { icon: '💰', color: 'c-teal',   title: 'More Money Back at Tax Time',   desc: "Properly categorized expenses and year-round bookkeeping means your CPA finds every deduction — and your tax bill shrinks." },
                ].map((f, i) => (
                  <div key={i} className="bk-feat">
                    <div className={`bk-feat-icon ${f.color}`}>{f.icon}</div>
                    <div><h4>{f.title}</h4><p>{f.desc}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bk-spotlight-right">
              <div className="bk-visual-card">
                <div className="bk-visual-dots" />
                <div className="bk-visual-metric-box">
                  <div className="bk-vm-label">Q4 Gross Revenue</div>
                  <div className="bk-vm-value">$284,500</div>
                  <div className="bk-vm-delta"><span>↑ 31%</span> vs Q4 last year</div>
                </div>
                <div className="bk-visual-insight">
                  <div className="bk-vi-header">
                    <span className="bk-vi-title">Expense Breakdown</span>
                    <span className="bk-vi-badge">Oct–Dec 2025</span>
                  </div>
                  {[
                    { label: 'Operations', pct: 38, color: '#2563eb' },
                    { label: 'Payroll',    pct: 52, color: '#4f46e5' },
                    { label: 'Marketing',  pct: 24, color: '#7c3aed' },
                    { label: 'Overhead',   pct: 16, color: '#0d9488' },
                  ].map((bar, i) => (
                    <div key={i} className="bk-bar-row">
                      <span className="bk-bar-label">{bar.label}</span>
                      <div className="bk-bar-track">
                        <div className="bk-bar-fill" style={{ width: `${bar.pct}%`, background: bar.color }} />
                      </div>
                      <span className="bk-bar-val">{bar.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="bk-section bk-process-bg">
        <div className="bk-container">
          <motion.div className="bk-section-header" {...fadeUp}>
            <div className="bk-kicker">Getting Started</div>
            <h2>Clean books in 4 simple steps</h2>
            <p>No lengthy onboarding. No disruption to your business. We get your books organized and current, typically within the first week.</p>
          </motion.div>
          <div className="bk-steps">
            {[
              { num: '1', title: 'Connect Your Accounts',  desc: 'Securely link your bank accounts, credit cards, and accounting software. We support QuickBooks, Xero, Wave, and more.' },
              { num: '2', title: 'Meet Your Bookkeeper',   desc: 'A dedicated bookkeeper reviews your business, learns your chart of accounts, and gets your historical records organized.' },
              { num: '3', title: 'We Handle the Books',    desc: 'Every transaction is categorized, reconciled, and entered daily. You receive clean monthly financial statements, on time.' },
              { num: '4', title: 'Grow with Confidence',   desc: 'Make smarter decisions backed by accurate financials. Walk into every tax season and investor meeting fully prepared.' },
            ].map((step, i) => (
              <motion.div key={i} className="bk-step" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}>
                <div className="bk-step-num">{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Accuracy & Trust (dark) ── */}
      <section className="bk-section bk-trust-bg">
        <div className="bk-trust-glow1" />
        <div className="bk-trust-glow2" />
        <div className="bk-container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div className="bk-section-header" {...fadeUp}>
            <div className="bk-kicker">Accuracy &amp; Security</div>
            <h2>Your financial data, protected and precise</h2>
            <p>We don't just keep your books — we keep them right. Accuracy, security, and reliability aren't features. They're our baseline.</p>
          </motion.div>
          <div className="bk-trust-grid">
            {trustItems.map((item, i) => (
              <motion.div key={i} className="bk-trust-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                <span className="bk-trust-icon-wrap">{item.icon}</span>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="bk-section bk-testimonials-bg">
        <div className="bk-container">
          <motion.div className="bk-section-header" {...fadeUp}>
            <div className="bk-kicker">Client Stories</div>
            <h2>Real businesses. Real results.</h2>
            <p>From catching up on months of overdue books to discovering hidden profits, hear what small business owners say about working with BizServe.</p>
          </motion.div>
          <div className="bk-testimonials-grid">
            {testimonials.map((t, i) => (
              <motion.div key={i} className="bk-tcard" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <div className="bk-stars">★★★★★</div>
                <p className="bk-tcard-text">{t.text}</p>
                <div className="bk-tcard-author">
                  <div className="bk-author-avatar" style={{ background: t.color }}>{t.initials}</div>
                  <div>
                    <div className="bk-author-name">{t.name}</div>
                    <div className="bk-author-role">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bk-section bk-cta-bg">
        <div className="bk-container">
          <motion.div className="bk-cta-inner" {...fadeUp}>
            <div className="bk-cta-glow1" />
            <div className="bk-cta-glow2" />
            <h2>Your books deserve better than a spreadsheet.</h2>
            <p>Get started today and have accurate, current financials delivered to your inbox every month — without lifting a finger.</p>
            <div className="bk-cta-btns">
              <button className="bk-cta-primary">Get started today →</button>
              <button className="bk-cta-secondary">Schedule a free consultation</button>
            </div>
            <div className="bk-cta-trust">
              {['30-day money back guarantee', 'Dedicated bookkeeper assigned', 'Books current within 7 days', 'Cancel anytime'].map((item, i) => (
                <div key={i} className="bk-cta-trust-item">
                  <span className="check">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

/* ─────────────────────────────────────────
   ROUTER SHELL
───────────────────────────────────────── */
const bookkeepingServicePages: Record<string, React.FC<ServicePageProps>> = {
  'daily-bookkeeping':   DailyBookkeepingPage,
  'financial-reporting': FinancialReportingPage,
  'accounts-management': AccountsManagementPage,
  'bank-reconciliation': BankReconciliationPage,
  'expense-tracking':    ExpenseTrackingPage,
  'tax-ready-books':     TaxReadyBooksPage,
};

const Bookkeeping: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceSlug = (searchParams.get('service') || '').toLowerCase();

  const handleSelectService = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('service', slug);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('service');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const ServicePage = serviceSlug ? bookkeepingServicePages[serviceSlug] : null;

  return (
    <div className="bk-page">
      <style>{styles}</style>
      {ServicePage
        ? <ServicePage onBack={handleBack} />
        : <BookkeepingOverview onSelectService={handleSelectService} />
      }
    </div>
  );
};

export default Bookkeeping;
